/**
 * StepKind → VariableType Compatibility
 *
 * Single source of truth for which V2 screen kinds can output which variable types.
 * Selection screens (List/Grid picks) always store semantic strings via valueMapping,
 * NOT numeric indices.
 *
 * @module v2-variable-utils
 */

import type { VariableType, StepKind } from '@segmently/schema-models/v2';

/**
 * StepKind → compatible VariableType[] mapping.
 *
 * Only screen kinds that collect user input are listed here.
 * Display-only screens (HeroContent, Carousel, etc.) are absent.
 */
export const STEP_KIND_TO_VARIABLE_TYPES: Readonly<Record<string, readonly VariableType[]>> = {
  // ═══════════════════════════════════════════════════════════════════
  // SINGLE SELECTION — stores one semantic string via valueMapping
  // ═══════════════════════════════════════════════════════════════════
  'ListSinglePick':  ['enum'],
  'GridSinglePick':  ['enum'],

  // ═══════════════════════════════════════════════════════════════════
  // MULTI SELECTION — stores array of semantic strings via valueMapping
  // ═══════════════════════════════════════════════════════════════════
  'ListMultiPick':   ['enum[]'],
  'GridMultiPick':   ['enum[]'],

  // ═══════════════════════════════════════════════════════════════════
  // WHEEL PICKER — stores selected string directly (no index mapping)
  // ═══════════════════════════════════════════════════════════════════
  'WheelPicker':     ['enum', 'string'],

  // ═══════════════════════════════════════════════════════════════════
  // TEXT INPUT — stores free text or validated number
  // ═══════════════════════════════════════════════════════════════════
  'TextInput':       ['string', 'number'],

  // ═══════════════════════════════════════════════════════════════════
  // WEB EMBED — per-input variable binding, any type possible
  // ═══════════════════════════════════════════════════════════════════
  'WebEmbed':        ['string', 'number', 'boolean', 'date', 'enum', 'enum[]'],
} as const;

/**
 * StepKinds that produce NO output (display-only screens).
 */
export const DISPLAY_ONLY_STEP_KINDS: readonly string[] = [
  'HeroContent',
  'Carousel',
  'ProgressSteps',
  'HeroList',
  'Paywall',
  'FlexibleLayout',
] as const;

/**
 * StepKinds that REQUIRE valueMapping in VariableBinding.
 * These screens store indices internally; the mapping translates to semantic strings.
 */
export const REQUIRES_VALUE_MAPPING: readonly string[] = [
  'ListSinglePick',
  'GridSinglePick',
  'ListMultiPick',
  'GridMultiPick',
] as const;

/**
 * StepKinds that support direct binding (no valueMapping needed).
 */
export const DIRECT_BINDING_STEP_KINDS: readonly string[] = [
  'TextInput',
  'WheelPicker',
] as const;

/**
 * StepKinds that use per-input variable binding (WebEmbed pattern).
 * Each EmbedInput has its own optional variableId.
 */
export const PER_INPUT_BINDING_STEP_KINDS: readonly string[] = [
  'WebEmbed',
] as const;

/**
 * All StepKinds that can output variables (union of all binding patterns).
 */
export const OUTPUT_CAPABLE_STEP_KINDS: readonly string[] = [
  ...REQUIRES_VALUE_MAPPING,
  ...DIRECT_BINDING_STEP_KINDS,
  ...PER_INPUT_BINDING_STEP_KINDS,
] as const;
